// Статусы сессии. В базе и в обмене с сервером — коды; подписи для экрана — STATUS_LABEL.
import { dayNum, hmToMin } from './dates.ts';

export const STATUS = {
  PLAN: 'plan',
  DONE: 'done',
  CANCEL_OK: 'cancel_ok',
  CANCEL_LATE: 'cancel_late',
  NO_SHOW: 'no_show',
  CANCEL_PSY: 'cancel_psy',
  RESERVE: 'reserve',
} as const;
export type Status = typeof STATUS[keyof typeof STATUS];

export const STATUSES: readonly Status[] = Object.values(STATUS);

export const STATUS_LABEL: Readonly<Record<Status, string>> = {
  plan: 'Запланирована',
  done: 'Прошла',
  cancel_ok: 'Отменена вовремя',
  cancel_late: 'Отменена поздно',
  no_show: 'Не пришёл',
  cancel_psy: 'Отменил психолог',
  reserve: 'Резерв',
};

export function isStatus(s: unknown): s is Status {
  return typeof s === 'string' && (STATUSES as readonly string[]).includes(s);
}

// Время занято: запланирована, прошла, не пришёл, резерв. Отменённые освобождают окно.
export function occupies(status: Status): boolean {
  return status === STATUS.PLAN || status === STATUS.DONE || status === STATUS.NO_SHOW || status === STATUS.RESERVE;
}

// Сессия, а не резерв: считается в «N сессий», в счётчиках клиента и группы.
export function isHeld(status: Status): boolean {
  return occupies(status) && status !== STATUS.RESERVE;
}

// Сессия с номером — как у Дарьи в календаре: прошедшие, запланированные и оплаченные отмены
// («№5 · отмена с оплатой»); отмена без оплаты номера не получает.
export function takesNumber(status: Status): boolean {
  return status === STATUS.DONE || status === STATUS.PLAN || status === STATUS.CANCEL_LATE;
}

// Считается в «N сессий» за месяц и у группы: проведённые и оплаченные поздние отмены.
export function countsAsSession(status: Status): boolean {
  return isHeld(status) || status === STATUS.CANCEL_LATE;
}

interface Timed { status: Status; date: string; start: string; dur?: number }

// Резерв ждёт решения психолога: до его времени меньше суток или оно уже прошло (сам резерв не снимается).
export function reserveDue(x: Timed, today: string, nowMin: number): boolean {
  return x.status === STATUS.RESERVE &&
    (dayNum(x.date) * 1440 + (hmToMin(x.start) ?? 0)) - (dayNum(today) * 1440 + nowMin) < 1440;
}

// Прошедшая сессия, которой так и не поставили статус.
export function isPastPlanned(x: Timed, today: string, nowMin: number): boolean {
  return x.status === STATUS.PLAN &&
    (x.date < today || (x.date === today && (hmToMin(x.start) ?? 0) + Number(x.dur ?? 0) <= nowMin));
}

// Отмена «вовремя» или «поздно» по порогу: startMs — начало сессии, nowMs — момент отмены.
export function cancelStatusByThreshold(startMs: number, nowMs: number, hours: number): Status {
  return startMs - nowMs >= (Number(hours) || 24) * 3600000 ? STATUS.CANCEL_OK : STATUS.CANCEL_LATE;
}
